import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const VERTICALS = ["REAL_ESTATE", "INSURANCE_FINANCIAL_SERVICES", "SALES_CAMPAIGNS", "CHARITY_CAMPAIGNS",
  "AUTOMOTIVE", "BUSINESS_DEVELOPMENT", "PROJECTS_PARTNERSHIPS"];
const LANGS = ["fr", "en", "es"];

export default function AdminSettings() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const canEdit = ["super_admin", "admin"].includes(user?.role);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => setSettings({}));
  }, []);

  const set = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  const toggleList = (key, value) => {
    const current = settings[key] || [];
    set(key, current.includes(value) ? current.filter((v) => v !== value) : [...current, value]);
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const payload = {
        company_name: settings.company_name,
        company_tagline_fr: settings.company_tagline_fr,
        company_tagline_en: settings.company_tagline_en,
        company_tagline_es: settings.company_tagline_es,
        contact_email: settings.contact_email,
        contact_phone: settings.contact_phone,
        company_address: settings.company_address,
        default_language: settings.default_language,
        active_languages: settings.active_languages,
        active_verticals: settings.active_verticals,
        auto_assign_new_candidates: settings.auto_assign_new_candidates,
        require_email_verification: settings.require_email_verification,
        privacy_consent_version: settings.privacy_consent_version,
      };
      const { data } = await api.put("/admin/settings", payload);
      setSettings(data);
      toast.success(t("admin.settingsSaved"));
    } catch (error) {
      toast.error(apiError(error, t("common.error")));
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;

  return (
    <div data-testid="admin-settings">
      <h1 className="font-display text-3xl font-light sm:text-4xl">{t("admin.settings")}</h1>

      <form onSubmit={save} className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.settingsCompany")}</h2>
          <div className="mt-4 space-y-4">
            <div>
              <Label htmlFor="company_name">{t("admin.settingsCompany")}</Label>
              <Input id="company_name" data-testid="settings-company-name" value={settings.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className="mt-1" />
            </div>
            {["fr", "en", "es"].map((code) => (
              <div key={code}>
                <Label htmlFor={`tagline_${code}`}>Tagline {code.toUpperCase()}</Label>
                <Input id={`tagline_${code}`} data-testid={`settings-tagline-${code}`} value={settings[`company_tagline_${code}`] || ""}
                       onChange={(e) => set(`company_tagline_${code}`, e.target.value)} className="mt-1" />
              </div>
            ))}
            <div>
              <Label htmlFor="contact_email">{t("contact.email")}</Label>
              <Input id="contact_email" data-testid="settings-contact-email" value={settings.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="company_address">{t("footer.addressLabel")}</Label>
              <Input id="company_address" data-testid="settings-company-address" value={settings.company_address || ""} onChange={(e) => set("company_address", e.target.value)} className="mt-1" />
            </div>
          </div>
        </section>

        <section className="border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.settingsLanguages")}</h2>
          <div className="mt-4 space-y-3">
            {LANGS.map((code) => (
              <label key={code} className="flex items-center gap-3 text-sm">
                <Checkbox data-testid={`settings-lang-${code}`} checked={(settings.active_languages || []).includes(code)} onCheckedChange={() => toggleList("active_languages", code)} />
                {code.toUpperCase()}
              </label>
            ))}
            <div>
              <Label htmlFor="default_language">{t("common.language")}</Label>
              <select id="default_language" data-testid="settings-default-language" value={settings.default_language || "fr"}
                      onChange={(e) => set("default_language", e.target.value)} className="mt-1 h-10 w-full border border-input bg-surface px-3 text-sm">
                {LANGS.map((code) => (
                  <option key={code} value={code}>{code.toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.settingsRecruitment")}</h2>
          <div className="mt-4 space-y-3">
            {VERTICALS.map((vertical) => (
              <label key={vertical} className="flex items-center gap-3 text-sm">
                <Checkbox data-testid={`settings-vertical-${vertical}`} checked={(settings.active_verticals || []).includes(vertical)} onCheckedChange={() => toggleList("active_verticals", vertical)} />
                {t(`verticals.${vertical}`)}
              </label>
            ))}
            <label className="flex items-center gap-3 text-sm">
              <Checkbox data-testid="settings-auto-assign" checked={Boolean(settings.auto_assign_new_candidates)} onCheckedChange={(v) => set("auto_assign_new_candidates", Boolean(v))} />
              auto_assign_new_candidates
            </label>
          </div>
        </section>

        <section className="border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.settingsPrivacy")}</h2>
          <div className="mt-4 space-y-4">
            <div>
              <Label htmlFor="consent_version">{t("apply.consent.version")}</Label>
              <Input id="consent_version" data-testid="settings-consent-version" value={settings.privacy_consent_version || ""} onChange={(e) => set("privacy_consent_version", e.target.value)} className="mt-1" />
            </div>
            <label className="flex items-center gap-3 text-sm">
              <Checkbox data-testid="settings-require-verification" checked={Boolean(settings.require_email_verification)} onCheckedChange={(v) => set("require_email_verification", Boolean(v))} />
              require_email_verification
            </label>
            <p className="text-xs text-muted-foreground">{t("admin.privacyNote")}</p>
          </div>
        </section>

        <section className="border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.settingsEmail")}</h2>
          <p className="mt-4 text-xs text-muted-foreground">{t("admin.emailNote")}</p>
          <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
            <li>EMAIL_FROM_NAME</li>
            <li>EMAIL_FROM_ADDRESS</li>
            <li>PUBLIC_APP_URL</li>
            <li>RESEND_API_KEY</li>
          </ul>
        </section>

        <section className="border border-border bg-surface p-5">
          <h2 className="font-display text-xl">{t("admin.settingsSecurity")}</h2>
          <p className="mt-4 text-xs text-muted-foreground">{t("admin.securityNote")}</p>
        </section>

        <div className="lg:col-span-2">
          <Button type="submit" disabled={busy || !canEdit} data-testid="settings-save" className="rounded-full bg-accent px-8 text-accent-foreground hover:bg-accent/90">
            {busy ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </form>
    </div>
  );
}
